<template>
    <vx-modal v-model:visible="visible" padding="standard" size="lg">
        <template v-slot:header>Groups</template>
        <div>
            <div class="flex gap-2 mb-3 lg:mb-6">
                <div class="grow" data-test="group-id-wrapper">
                    <vx-selectable
                        size="lg"
                        placeholder=""
                        :options="options"
                        :loading="searching"
                        :reduce="(option) => option.value"
                        :createOption="
                            (option) => {
                                return {
                                    label: option,
                                    value: option,
                                };
                            }
                        "
                        :getOptionKey="(option) => option.value"
                        :taggable="true"
                        @search="search"
                        v-model="groupId"
                        @option:selected="add()"
                        :error="displayFirstError(validate.groupId)"
                    >
                        <template v-slot:no-options> Search groups. </template>
                    </vx-selectable>
                </div>
                <vx-button @click="add()" :loading="adding" data-test="group-add" size="lg" type="button">
                    <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
            </div>

            <vx-alert v-if="error" color="danger" class="mb-3" :closeable="false">{{ error }}</vx-alert>
            <vx-icon-loading v-if="loadingGroups && !groups.length" spin></vx-icon-loading>
            <vx-alert :closeable="false" v-else-if="!groups.length">
                No groups were specified to this contact.
            </vx-alert>
            <div v-else>
                <vx-list-animated
                    v-model="groups"
                    :removeable="false"
                    label="group_id"
                    value="group_id"
                    @remove="remove"
                >
                    <template v-slot:item="{ item }">
                        <div
                            class="grid grid-cols-1 w-full gap-1.5 px-5 py-3 text-xs text-slate-900 md:grid-cols-5 md:gap-2"
                        >
                            <div class="line-clamp-3 break-all font-bold md:font-normal md:col-span-2">
                                {{ item.group_name || ' - ' }}
                            </div>
                            <vx-popover
                                hover
                                arrow
                                placement="top"
                                class="[&>div:first-child]:line-clamp-3 break-all text-xs"
                                :class="{
                                    'md:col-span-3': !item.is_preferred,
                                    'md:col-span-2': item.is_preferred,
                                }"
                            >
                                {{ item.group_id }}
                                <template v-slot:content>
                                    {{ item.group_id }}
                                </template>
                            </vx-popover>
                            <div
                                v-if="item.is_preferred"
                                class="flex justify-start w-full text-xs mt-1.5 md:mt-0 md:justify-end"
                            >
                                <vx-badge size="xs" color="violet">
                                    Preferred <font-awesome-icon widthAuto :icon="faStar"></font-awesome-icon>
                                </vx-badge>
                            </div>
                        </div>
                    </template>
                </vx-list-animated>
            </div>
        </div>
    </vx-modal>
</template>

<script setup>
import debounce from '~/utils/debounce';
import {
    VxAlert,
    VxButton,
    VxBadge,
    VxPopover,
    VxIconLoading,
    VxListAnimated,
    VxModal,
    VxSelectable,
} from '@voxie/frontend-components';
import { ref, nextTick, watch } from 'vue';
import { useContactDetails } from '~/components/contacts/contact/useContactDetails';
import useVuelidate from '@vuelidate/core';
import { maxLength, requiredIf } from '@vuelidate/validators';
import { alphanumericDashUnderscore, displayFirstError } from '~/utils/validation';
import { faCirclePlus, faStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { catchRequestError } from '~/utils/request';
import { groupsClient } from '~/services';
import { GROUP_STATUSES_MANAGEMENT } from '~/constants/groupStatuses';

const teamId = Spark.state.currentTeam.id;

const visible = defineModel('visible', {
    default: false,
});

const { groups, getGroups, loadingGroups, addGroup, deleteGroup } = useContactDetails();

watch(
    visible,
    () => {
        if (visible.value && !groups.value.length) {
            getGroups();
        }
    },
    {
        immediate: true,
    }
);

const error = ref('');
const groupId = ref('');
const options = ref([]);
const searching = ref(false);
const adding = ref(false);

const search = debounce((search) => {
    searching.value = true;

    groupsClient
        .getGroups(
            { teamId },
            {
                query: search,
                per_page: 10,
                setup_required: 0,
                status: GROUP_STATUSES_MANAGEMENT,
            }
        )
        .then((response) => {
            options.value = response.data.data.map(({ group_id, name }) => ({
                value: group_id,
                label: `${name} - ${group_id}`.trim(),
            }));
            searching.value = false;
        });
}, 200);

const validate = useVuelidate(
    {
        groupId: {
            requiredIf: requiredIf(adding),
            maxLength: maxLength(39),
            format: alphanumericDashUnderscore,
        },
    },
    {
        groupId: groupId,
    }
);

const add = async () => {
    if (adding.value) {
        return;
    }
    adding.value = true;
    await nextTick();

    const isValid = await validate.value.$validate();
    if (!isValid) {
        setTimeout(() => {
            adding.value = false;
        }, 3500);
        return;
    }

    try {
        await addGroup({ group_id: groupId.value });

        groupId.value = '';
        adding.value = false;
    } catch (e) {
        error.value = catchRequestError(e);
    }
};

const remove = async (group) => {
    try {
        await deleteGroup(group);
    } catch (e) {
        error.value = catchRequestError(e);
    }
};
</script>
